import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAction, useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  Code,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { hasAnyRole, isInstitutionAdministrator } from "../lib/roleUtils";
import { CANONICAL_OUTCOMES, getOutcomeLabel } from "../../src/reviewStandards.js";

const UPLOADED_FILE_STORAGE_KEY = "quickChartEvaluator:uploadedFile";

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const base64 = result.split(",")[1];
      if (!base64) {
        reject(new Error("Failed to convert file to base64."));
        return;
      }
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

function normalizeUserFileRecord(payload) {
  if (!payload) return null;
  if (payload.id) return payload;
  if (payload.userFile?.id) return payload.userFile;
  if (payload.data?.userFile?.id) return payload.data.userFile;
  if (payload.data?.id) return payload.data;
  if (payload.record?.id) return payload.record;
  return null;
}

function canAccess(user) {
  if (!user) return false;
  return hasAnyRole(user?.roles || user?.roleList || [], ["admin", "system-admin", "sysadmin"]) || isInstitutionAdministrator(user);
}

function prettyJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return String(value || "");
  }
}

function statusColor(status) {
  switch (status) {
    case "acceptable":
    case "pass":
    case "supported":
    case "yes":
    case "excellent":
    case "strong":
      return "green";
    case "acceptable_with_minor_issues":
    case "minor_issue":
    case "mostly":
    case "partially_supported":
    case "adequate":
      return "yellow";
    case "needs_review":
    case "unclear":
    case "not_applicable":
    case "marginal":
      return "orange";
    case "deficient":
    case "critical_concern":
    case "critical":
    case "not_supported":
    case "no":
    case "poor":
      return "red";
    default:
      return "gray";
  }
}

function DomainCard({ title, review }) {
  if (!review) return null;
  const details = Object.entries(review).filter(([key, value]) => {
    if (key === "status" || key === "applicability" || key === "rationale_quality") return true;
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && value !== undefined && value !== "";
  });

  return (
    <Card>
      <CardBody>
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between" align="flex-start">
            <Heading size="sm">{title}</Heading>
            <Badge colorScheme={statusColor(review.status || review.applicability)}>{review.status || review.applicability}</Badge>
          </HStack>
          {details.map(([key, value]) => (
            <Box key={key}>
              <Text fontSize="sm" fontWeight="semibold" textTransform="capitalize">
                {key.replace(/_/g, " ")}
              </Text>
              {Array.isArray(value) ? (
                value.length ? (
                  <VStack align="stretch" spacing={1} mt={1}>
                    {value.map((item, index) => (
                      <Text key={`${key}-${index}`} fontSize="sm" color="gray.300">
                        {typeof item === "string" ? `• ${item}` : prettyJson(item)}
                      </Text>
                    ))}
                  </VStack>
                ) : null
              ) : (
                <Text fontSize="sm" color="gray.300" mt={1}>
                  {String(value)}
                </Text>
              )}
            </Box>
          ))}
        </VStack>
      </CardBody>
    </Card>
  );
}

export default function AdminQuickChartEvaluator() {
  const user = useUser();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [chartText, setChartText] = useState("");
  const [chartMetadataText, setChartMetadataText] = useState("{\n  \"agency\": \"\",\n  \"sourcePlatform\": \"\",\n  \"incidentState\": \"\"\n}");
  const [forceTennesseeProtocolCheck, setForceTennesseeProtocolCheck] = useState(false);
  const [forceCmsMedicalNecessityReview, setForceCmsMedicalNecessityReview] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
  const [humanReviewDraft, setHumanReviewDraft] = useState({
    humanOutcome: "acceptable",
    explanation: "",
    disagreementNotes: "",
  });

  const [{ fetching: evaluating }, submitEvaluation] = useGlobalAction(api.submitQuickChartEvaluation);
  const [{ data: listData, fetching: loadingList }, listEvaluations] = useGlobalAction(api.listQuickChartEvaluations);
  const [{ data: detailData, fetching: loadingDetail }, getEvaluation] = useGlobalAction(api.getQuickChartEvaluation);
  const [{ fetching: submittingHumanReview }, submitQaHumanReview] = useGlobalAction(api.submitQaHumanReview);
  const [{ fetching: creatingUserFile }, createUserFile] = useAction(api.userFile.create);
  const [{ }, getCurrentUserAccessProfile] = useGlobalAction(api.getCurrentUserAccessProfile);

  useEffect(() => {
    (async () => {
      try {
        const profile = await getCurrentUserAccessProfile({});
        setCurrentUserProfile(profile?.user ? profile : profile?.profile || null);
      } catch (_error) {
        setCurrentUserProfile(null);
      }
    })();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = window.sessionStorage.getItem(UPLOADED_FILE_STORAGE_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (parsed?.id) {
        setUploadedFile(parsed);
      }
    } catch (_error) {
      window.sessionStorage.removeItem(UPLOADED_FILE_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    listEvaluations({ first: 50 });
  }, []);

  useEffect(() => {
    if (!selectedEvaluationId) return;
    getEvaluation({ evaluationId: selectedEvaluationId });
  }, [selectedEvaluationId]);

  useEffect(() => {
    if (detailData?.evaluation?.evaluationResult) {
      setResult(detailData.evaluation.evaluationResult);
    }
  }, [detailData?.evaluation?.id]);

  useEffect(() => {
    const humanReview = detailData?.evaluation?.qaChart?.humanReviewSummary;
    if (!humanReview) return;
    setHumanReviewDraft({
      humanOutcome: humanReview.humanOutcome || "acceptable",
      explanation: humanReview.explanation || "",
      disagreementNotes: humanReview.reviewNotes || "",
    });
  }, [detailData?.evaluation?.id]);

  const canUsePage = canAccess(user);
  const overallStatus = result?.chart_review?.overall_status || null;
  const materialIssues = result?.material_issues || [];
  const protocolFindings = result?.jurisdiction_protocol_check?.protocol_findings || [];
  const evaluations = listData?.evaluations || [];

  const summaryStats = useMemo(() => ({
    qualityTier: result?.chart_review?.quality_tier || "-",
    defensible: result?.final_determination?.professionally_defensible || "-",
    coherent: result?.final_determination?.clinically_coherent || "-",
    billing: result?.final_determination?.sufficient_for_billing_support || "-",
  }), [result]);

  const handleSubmitHumanReview = async () => {
    const chartId = detailData?.evaluation?.qaChart?.id;
    if (!chartId) return;
    try {
      await submitQaHumanReview({
        chartId,
        ...humanReviewDraft,
      });
      await listEvaluations({ first: 50 });
      await getEvaluation({ evaluationId: selectedEvaluationId });
      toast({
        title: "Human review saved",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Human review failed",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingFile(true);
    try {
      const ownerId = currentUserProfile?.user?.id || currentUserProfile?.id || user?.id;
      const institution = currentUserProfile?.user?.institution || currentUserProfile?.institution || user?.institution;
      if (!ownerId || !institution) {
        throw new Error("Could not resolve current user profile for upload.");
      }

      const timestamp = Date.now();
      const fileBase64 = await fileToBase64(file);
      const created = await createUserFile({
        file: {
          base64: fileBase64,
          fileName: file.name,
          mimeType: file.type || "application/pdf",
        },
        originalFilename: file.name,
        title: file.name,
        mimeType: file.type || "application/pdf",
        fileSizeBytes: file.size,
        category: "clinical_record",
        uploadDate: new Date().toISOString(),
        status: "active",
        isPublic: false,
        institution,
        owner: { _link: ownerId },
        studentId: ownerId,
        submissionSource: "portal",
        fileIdentifier: `quick-chart-evaluator-${timestamp}-${file.name}`,
        clinicalFileId: `quick-chart-evaluator-${timestamp}`,
      });

      const normalizedFile = normalizeUserFileRecord(created);

      if (!normalizedFile?.id) {
        throw new Error("Upload completed but the file record could not be resolved.");
      }

      setUploadedFile(normalizedFile);
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(UPLOADED_FILE_STORAGE_KEY, JSON.stringify(normalizedFile));
      }
      toast({
        title: "Chart uploaded",
        description: file.name,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleEvaluate = async () => {
    let chartMetadata = {};
    if (chartMetadataText.trim()) {
      try {
        chartMetadata = JSON.parse(chartMetadataText);
      } catch (error) {
        toast({
          title: "Metadata JSON is invalid",
          description: error.message,
          status: "error",
          duration: 5000,
        });
        return;
      }
    }

    if (!chartText.trim() && !uploadedFile?.id) {
      toast({
        title: "Nothing to evaluate",
        description: "Upload a chart file or paste chart text.",
        status: "warning",
        duration: 4000,
      });
      return;
    }

    try {
      const response = await submitEvaluation({
        chartFileId: uploadedFile?.id || undefined,
        chartText: chartText.trim() || undefined,
        chartMetadata,
        forceTennesseeProtocolCheck,
        forceCmsMedicalNecessityReview,
        sourceType: uploadedFile?.id ? "upload" : "manual_text",
        sourceSystem: "quickqa",
        title: uploadedFile?.originalFilename || undefined,
      });
      setResult(response?.result || null);
      setSelectedEvaluationId(response?.evaluationId || null);
      setUploadedFile(null);
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem(UPLOADED_FILE_STORAGE_KEY);
      }
      await listEvaluations({ first: 50 });
    } catch (error) {
      toast({
        title: "Evaluation failed",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  if (!canUsePage) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>Access denied</AlertTitle>
            <AlertDescription>This evaluator is limited to administrators and institutional administrators.</AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg">Quick Chart Evaluation</Heading>
          <Text color="gray.400" mt={2}>
            Upload any completed EMS chart/PCR or paste the chart text to get a professional QA review without using template extraction.
          </Text>
        </Box>

        <Grid templateColumns={{ base: "1fr", xl: "0.95fr 1.15fr" }} gap={6}>
          <GridItem>
            <VStack align="stretch" spacing={4}>
              <Card>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <FormControl>
                      <FormLabel>Upload chart/PDF</FormLabel>
                      <Input ref={fileInputRef} type="file" accept=".pdf,.txt,.json,.xml,.csv" onChange={handleFileUpload} />
                      <FormHelperText>
                        Optional. Native extracted text is used first; short PDFs can fall back to vision supplement extraction.
                      </FormHelperText>
                    </FormControl>

                    {isUploadingFile || creatingUserFile ? (
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>Uploading chart attachment...</AlertDescription>
                      </Alert>
                    ) : null}

                    {uploadedFile?.id ? (
                      <Alert status="success" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>
                          Uploaded file: <Code>{uploadedFile.originalFilename || uploadedFile.title || uploadedFile.id}</Code>
                        </AlertDescription>
                      </Alert>
                    ) : null}

                    <FormControl>
                      <FormLabel>Paste chart text</FormLabel>
                      <Textarea
                        minH="220px"
                        value={chartText}
                        onChange={(event) => setChartText(event.target.value)}
                        placeholder="Paste the chart narrative, PCR export text, or copied document content here."
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Optional chart metadata JSON</FormLabel>
                      <Textarea
                        minH="140px"
                        fontFamily="mono"
                        value={chartMetadataText}
                        onChange={(event) => setChartMetadataText(event.target.value)}
                      />
                    </FormControl>

                    <Stack spacing={3}>
                      <Checkbox
                        isChecked={forceTennesseeProtocolCheck}
                        onChange={(event) => setForceTennesseeProtocolCheck(event.target.checked)}
                      >
                        Force Tennessee protocol check
                      </Checkbox>
                      <Checkbox
                        isChecked={forceCmsMedicalNecessityReview}
                        onChange={(event) => setForceCmsMedicalNecessityReview(event.target.checked)}
                      >
                        Force CMS medical necessity review
                      </Checkbox>
                    </Stack>

                    <Button
                      colorScheme="blue"
                      onClick={handleEvaluate}
                      isLoading={evaluating}
                      isDisabled={isUploadingFile || creatingUserFile}
                      loadingText="Evaluating chart"
                    >
                      Run Quick Chart Evaluation
                    </Button>
                  </VStack>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <Heading size="sm">Recent QuickQA Evaluations</Heading>
                    {loadingList ? <Spinner /> : null}
                    <TableContainer>
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th>Chart</Th>
                            <Th>Status</Th>
                            <Th>Source</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {evaluations.map((evaluation) => (
                            <Tr
                              key={evaluation.id}
                              cursor="pointer"
                              bg={selectedEvaluationId === evaluation.id ? "whiteAlpha.200" : "transparent"}
                              onClick={() => setSelectedEvaluationId(evaluation.id)}
                            >
                              <Td>
                                <Text fontSize="sm" fontWeight="semibold">{evaluation.title}</Text>
                                <Text fontSize="xs" color="gray.400">{new Date(evaluation.createdAt).toLocaleString()}</Text>
                              </Td>
                              <Td>
                                <HStack spacing={2}>
                                  {evaluation.reviewPresentation?.processingState ? <Badge colorScheme={evaluation.reviewPresentation.processingState.colorScheme}>{evaluation.reviewPresentation.processingState.label}</Badge> : null}
                                  {evaluation.reviewPresentation?.reviewState ? <Badge colorScheme={evaluation.reviewPresentation.reviewState.colorScheme}>{evaluation.reviewPresentation.reviewState.label}</Badge> : null}
                                  {evaluation.reviewPresentation?.effectiveOutcome ? <Badge colorScheme={evaluation.reviewPresentation.effectiveOutcome.colorScheme}>{evaluation.reviewPresentation.effectiveOutcome.label}</Badge> : null}
                                </HStack>
                              </Td>
                              <Td>
                                <Text fontSize="sm">{evaluation.sourceType}</Text>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </GridItem>

          <GridItem>
            {!result && !(evaluating || creatingUserFile) ? (
              <Card>
                <CardBody>
                  <Text color="gray.400">
                    Results will appear here. The evaluator returns structured QA findings, protocol notes, billing applicability, and reviewer questions.
                  </Text>
                </CardBody>
              </Card>
            ) : null}

            {evaluating || loadingDetail ? (
              <Card>
                <CardBody>
                  <HStack spacing={3}>
                    <Spinner />
                    <Text>Running quick chart evaluator...</Text>
                  </HStack>
                </CardBody>
              </Card>
            ) : null}

            {result ? (
              <VStack align="stretch" spacing={4}>
                {detailData?.evaluation ? (
                  <Card>
                    <CardBody>
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                        <Box>
                          <Text fontSize="sm" color="gray.400">Stored Record</Text>
                          <Code>{detailData.evaluation.id}</Code>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.400">Source Email</Text>
                          <Text>{detailData.evaluation.sourceEmail || "-"}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.400">Notification</Text>
                          <Badge colorScheme={statusColor(detailData.evaluation.notificationStatus?.status)}>
                            {detailData.evaluation.notificationStatus?.status || "not_requested"}
                          </Badge>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.400">Agency QA Backbone</Text>
                          <Badge colorScheme={statusColor(detailData.evaluation.workflowBackboneStatus)}>
                            {detailData.evaluation.workflowBackboneStatus || "not_linked"}
                          </Badge>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.400">QA Workflow</Text>
                          <Text>{detailData.evaluation.qaWorkflow?.name || "-"}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.400">QA Chart</Text>
                          <Text>{detailData.evaluation.qaChart?.title || detailData.evaluation.qaChart?.id || "-"}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.400">Human Review</Text>
                          {detailData.evaluation.reviewPresentation?.humanOutcome ? (
                            <Badge colorScheme={detailData.evaluation.reviewPresentation.humanOutcome.colorScheme}>
                              {detailData.evaluation.reviewPresentation.humanOutcome.label}
                            </Badge>
                          ) : (
                            <Text>Not reviewed</Text>
                          )}
                        </Box>
                      </SimpleGrid>
                    </CardBody>
                  </Card>
                ) : null}

                <Card>
                  <CardBody>
                    <VStack align="stretch" spacing={3}>
                      <HStack justify="space-between" align="flex-start">
                        <Box>
                          <Heading size="md">Overall Review</Heading>
                          <Text color="gray.300" mt={2}>{result.chart_review?.summary}</Text>
                        </Box>
                        <Badge colorScheme={statusColor(overallStatus)}>{overallStatus}</Badge>
                      </HStack>
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                        <Box><Text fontSize="sm" color="gray.400">Quality</Text><Badge colorScheme={statusColor(summaryStats.qualityTier)}>{summaryStats.qualityTier}</Badge></Box>
                        <Box><Text fontSize="sm" color="gray.400">Defensible</Text><Badge colorScheme={statusColor(summaryStats.defensible)}>{summaryStats.defensible}</Badge></Box>
                        <Box><Text fontSize="sm" color="gray.400">Coherent</Text><Badge colorScheme={statusColor(summaryStats.coherent)}>{summaryStats.coherent}</Badge></Box>
                        <Box><Text fontSize="sm" color="gray.400">Billing Support</Text><Badge colorScheme={statusColor(summaryStats.billing)}>{summaryStats.billing}</Badge></Box>
                      </SimpleGrid>
                      {result.chart_review?.manual_review_required ? (
                        <Alert status="warning" borderRadius="md">
                          <AlertIcon />
                          <Box>
                            <AlertTitle>Manual review required</AlertTitle>
                            <AlertDescription>
                              {(result.chart_review?.manual_review_reasons || []).join(" | ") || "The evaluator requested manual review."}
                            </AlertDescription>
                          </Box>
                        </Alert>
                      ) : null}
                    </VStack>
                  </CardBody>
                </Card>

                {detailData?.evaluation?.qaChart?.id ? (
                  <Card>
                    <CardBody>
                      <VStack align="stretch" spacing={3}>
                        <Heading size="sm">Overall Human Review</Heading>
                        <FormControl>
                          <FormLabel>Final Outcome</FormLabel>
                          <Select value={humanReviewDraft.humanOutcome} onChange={(event) => setHumanReviewDraft((current) => ({ ...current, humanOutcome: event.target.value }))}>
                            {CANONICAL_OUTCOMES.map((outcome) => (
                              <option key={outcome} value={outcome}>{getOutcomeLabel(outcome)}</option>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Reviewer Explanation</FormLabel>
                          <Textarea value={humanReviewDraft.explanation} onChange={(event) => setHumanReviewDraft((current) => ({ ...current, explanation: event.target.value }))} minH="120px" />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Disagreement / Training Notes</FormLabel>
                          <Textarea value={humanReviewDraft.disagreementNotes} onChange={(event) => setHumanReviewDraft((current) => ({ ...current, disagreementNotes: event.target.value }))} minH="90px" />
                        </FormControl>
                        <Button colorScheme="purple" onClick={handleSubmitHumanReview} isLoading={submittingHumanReview}>Save Human Review</Button>
                      </VStack>
                    </CardBody>
                  </Card>
                ) : null}

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <DomainCard title="Assessment" review={result.domain_reviews?.assessment} />
                  <DomainCard title="Impression / Diagnosis" review={result.domain_reviews?.impression_diagnosis} />
                  <DomainCard title="Clinical Management" review={result.domain_reviews?.clinical_management} />
                  <DomainCard title="Reassessment / Response" review={result.domain_reviews?.reassessment_response} />
                  <DomainCard title="Transport / Disposition" review={result.domain_reviews?.transport_disposition} />
                  <DomainCard title="Documentation Defensibility" review={result.domain_reviews?.documentation_defensibility} />
                  <DomainCard title="Billing / Medical Necessity" review={result.domain_reviews?.billing_medical_necessity} />
                </SimpleGrid>

                <Card>
                  <CardBody>
                    <VStack align="stretch" spacing={3}>
                      <HStack justify="space-between">
                        <Heading size="sm">Jurisdiction / Protocol Check</Heading>
                        <Badge colorScheme={statusColor(result.jurisdiction_protocol_check?.tennessee_based)}>
                          TN: {result.jurisdiction_protocol_check?.tennessee_based}
                        </Badge>
                      </HStack>
                      <Text color="gray.300">{result.jurisdiction_protocol_check?.basis}</Text>
                      {(protocolFindings || []).length ? (
                        <VStack align="stretch" spacing={2}>
                          {protocolFindings.map((finding, index) => (
                            <Box key={`protocol-${index}`} borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="md" p={3}>
                              <HStack justify="space-between" align="flex-start">
                                <Text fontWeight="semibold">{finding.topic}</Text>
                                <Badge colorScheme={statusColor(finding.status)}>{finding.status}</Badge>
                              </HStack>
                              <Text fontSize="sm" color="gray.300" mt={2}>{finding.finding}</Text>
                              {finding.reference ? <Code mt={2} display="block" whiteSpace="pre-wrap">{finding.reference}</Code> : null}
                            </Box>
                          ))}
                        </VStack>
                      ) : (
                        <Text color="gray.400">No protocol findings returned.</Text>
                      )}
                    </VStack>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <VStack align="stretch" spacing={3}>
                      <Heading size="sm">Material Issues</Heading>
                      {materialIssues.length ? materialIssues.map((issue, index) => (
                        <Box key={`issue-${index}`} borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="md" p={3}>
                          <HStack justify="space-between" align="flex-start">
                            <Text fontWeight="semibold">{issue.issue}</Text>
                            <Badge colorScheme={statusColor(issue.severity)}>{issue.severity}</Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.300" mt={2}>{issue.why_it_matters}</Text>
                          <Text fontSize="sm" mt={2}>Recommended fix: {issue.recommended_fix}</Text>
                        </Box>
                      )) : <Text color="gray.400">No material issues returned.</Text>}
                    </VStack>
                  </CardBody>
                </Card>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Card>
                    <CardBody>
                      <Heading size="sm" mb={3}>Reviewer Questions</Heading>
                      {(result.reviewer_questions || []).length ? (
                        <VStack align="stretch" spacing={2}>
                          {result.reviewer_questions.map((question, index) => <Text key={`question-${index}`}>• {question}</Text>)}
                        </VStack>
                      ) : <Text color="gray.400">No reviewer questions returned.</Text>}
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody>
                      <Heading size="sm" mb={3}>Recommended Improvements</Heading>
                      {(result.recommended_improvements || []).length ? (
                        <VStack align="stretch" spacing={2}>
                          {result.recommended_improvements.map((item, index) => <Text key={`improvement-${index}`}>• {item}</Text>)}
                        </VStack>
                      ) : <Text color="gray.400">No recommended improvements returned.</Text>}
                    </CardBody>
                  </Card>
                </SimpleGrid>

                <Card>
                  <CardBody>
                    <Heading size="sm" mb={3}>Metadata</Heading>
                    <Code display="block" whiteSpace="pre-wrap">{prettyJson(result.metadata)}</Code>
                  </CardBody>
                </Card>
              </VStack>
            ) : null}
          </GridItem>
        </Grid>
      </VStack>
    </Box>
  );
}
